import React, { FC } from 'react';
import './GenericDomainStack.css';
import { StackMenu, Widget } from '@/Shared/UI/Components';
import { DomainType, WidgetFormat } from '@/Shared/Types';
import { DOMAIN_CONFIGS } from '@/Shared/Config/domain-config';

interface GenricDomainStackProps { 
	allowAll? : boolean;
	activeKey? : DomainType;
	format ? : WidgetFormat;
	homeMode? : boolean;
}


/**
 * Ordered list of supported domain stack keys.
 * The order defines the display priority in the stack menu.
 */
const STACK_KEYS = [
	'Activity',
	'Need',
	'Property',
	'Matching',
	'Contact'
] as DomainType[];


/**
 * Maximum number of stacked widgets depending on context:
 * - Home mode: more contextual overview
 * - Detail mode: reduced stack to preserve focus
 */
const HOME_MODE_MAX_STACK_ITEMS = 5;
const DETAIL_MODE_MAX_STACK_ITEMS = 4;


/**
 * GenricDomainStack
 *
 * High-level layout component responsible for rendering
 * a stacked set of domain widgets (Activity, Need, Property, etc.).
 *
 * This component is purely compositional:
 * - It does not fetch data
 * - It delegates rendering to `StackMenu` and `Widget`
 *
 * Responsibilities:
 * - Determine which domain widgets should be displayed
 * - Ensure the active domain (if any) is prioritized
 * - Adapt the stack size depending on the UI context (home vs detail)
 *
 * Typical use cases:
 * - Home dashboard overview
 * - Contextual domain navigation inside detail pages
 * 
 * Author: Samano CASTRE
 * Date: January 2026
 */
const GenricDomainStack: FC<GenricDomainStackProps> = ({
	allowAll = true, 
	activeKey, 
	format = 'Card', 
	homeMode = false
}) => {
     

	/**
	 * Resolve the maximum number of stack items
	 * based on the current display context
	 */
	const MAX_ITEM = homeMode 
	? HOME_MODE_MAX_STACK_ITEMS 
	: DETAIL_MODE_MAX_STACK_ITEMS;
	

	/**
	 * Builds the stack menu items:
	 * - Ensures the active domain (if provided) is always first
	 * - Completes the stack with remaining domains in priority order
	 * - Stops once the maximum number of items is reached
	 */
	const getStackItems = () => {

		const items = [];

		// Always prioritize the active domain if present
		if(activeKey) {
			items.push({
				type : activeKey,
				label : DOMAIN_CONFIGS[activeKey].domainLabel
			});
		}

		// Fill the remaining stack slots with other domains
		for (let index = 0; index < STACK_KEYS.length; index++) {
			if(items.length >= MAX_ITEM) {
				break;
			}
			const element = STACK_KEYS[index];
			if (element !== activeKey ) {

				items.push({
					type : element,
					label : DOMAIN_CONFIGS[element].domainLabel
				});
			}
		}
		
		return items;
	}

	/**
	 * Precompute stack keys for widget metadata (title, label)
	 */
	const stackItems = getStackItems();
	
	return (
		<Widget
		    domainLabel={stackItems[0].label}
			title={ allowAll? undefined : 'Récents'} 
			classes={`GenricDomainStack ${homeMode ? 'homeMode' : 'detailMode'}`}
			content={
				<StackMenu
				    
					format={format}
					allowAll = {allowAll} 
					stackItems = {stackItems}
				/>
			}
		/>
	);
}

export default GenricDomainStack;